import { useId } from "react";
import { TimeWheelInput } from "./TimeWheelInput";

export type CollectionHoursDraft = {
  days: string;
  from: string;
  until: string;
  custom: boolean;
  text: string;
};

// [items.pickup-defaults](../../../docs/product/items.md#items-pickup-defaults): posting accepts empty/custom hours, but picker ranges must
// have both endpoints and a nonzero duration before they can be serialized.
export function collectionHoursError(draft: CollectionHoursDraft): string | null {
  if (draft.custom) return null;
  if (Boolean(draft.from) !== Boolean(draft.until)) return "Choose both opening and closing times, or clear them.";
  if (draft.from && draft.from === draft.until) return "Choose different opening and closing times.";
  return null;
}

function displayTime(time: string): string {
  const [hour, minute] = time.split(":").map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

// [items.pickup-defaults](../../../docs/product/items.md#items-pickup-defaults): posting and purchase summaries share readable Philippine-time
// hours. Preserve custom schedules and mark overnight ends without UTC conversion.
export function collectionHoursText(draft: CollectionHoursDraft): string {
  if (draft.custom) return draft.text.trim();
  if (!draft.from || !draft.until || collectionHoursError(draft)) return "";
  return `${draft.days}, ${displayTime(draft.from)}–${displayTime(draft.until)}${draft.until < draft.from ? " (next day)" : ""} (Philippine time)`;
}

export function CollectionHoursInput({ value, onChange }: {
  value: CollectionHoursDraft;
  onChange: (value: CollectionHoursDraft) => void;
}) {
  const daysId = useId();
  const summary = collectionHoursText(value);
  return <fieldset className="min-w-0 space-y-3">
    <legend className="text-sm font-medium text-navy">Collection hours <span className="text-xs font-normal text-mut">(optional)</span></legend>
    {value.custom ? <label className="block text-xs text-mut">Schedule
      <input className="field mt-1.5" maxLength={300} value={value.text} onChange={(event) => onChange({ ...value, text: event.target.value })} placeholder="e.g. Tue & Thu, 10 AM–2 PM; Sat by appointment" />
    </label> : <>
      <div>
        <label htmlFor={daysId} className="block text-xs text-mut">Days</label>
        <select id={daysId} className="field mt-1.5" value={value.days} onChange={(event) => onChange({ ...value, days: event.target.value })}>
          <option>Every day</option><option>Mon–Fri</option><option>Sat–Sun</option>
        </select>
      </div>
      <div className="grid min-w-0 grid-cols-2 gap-3">
        <TimeWheelInput label="From" dialogLabel="Collection starts" value={value.from} onChange={(from) => onChange({ ...value, from })} />
        <TimeWheelInput label="Until" dialogLabel="Collection ends" value={value.until} onChange={(until) => onChange({ ...value, until })} />
      </div>
      <p className="text-xs text-mut">{summary || "Philippine time"}</p>
    </>}
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      <button type="button" className="font-medium text-navy underline underline-offset-4" onClick={() => onChange({ ...value, custom: !value.custom })}>{value.custom ? "Use time pickers" : "Enter custom hours"}</button>
      {(value.from || value.until || value.text) && <button type="button" className="text-mut underline underline-offset-4" onClick={() => onChange({ ...value, from: "", until: "", text: "" })}>Clear hours</button>}
    </div>
  </fieldset>;
}
